import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";
import Shop from "../components/Shop";
import { useLanguage } from "../i18n/LanguageContext";
import { Brain, ScanFace, Target, ChevronRight } from "lucide-react";

const heroImg = "https://customer-assets.emergentagent.com/job_self-mastery-hub-8/artifacts/8z530lxl_1000041818.jpg";
const aboutImg = "/assets/enrico_suit_clean.png";

const icons = [Brain, ScanFace, Target];

const MenPage = () => {
  const { t } = useLanguage();
  const m = t.men;

  return (
    <div data-testid="men-page" className="theme-men min-h-screen">
      <Header variant="men" />

      {/* HERO */}
      <section className="px-6 md:px-12 lg:px-20 pt-10 md:pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 fade-up">
          <span className="eyebrow text-[#8A91A5]">{m.hero.eyebrow}</span>
          <h1 className="mt-6 text-4xl md:text-6xl lg:text-7xl leading-[1.02] text-white" style={{ fontFamily: 'Clash Display, Cabinet Grotesk, sans-serif', fontWeight: 600 }}>
            {m.hero.title}
          </h1>
          <p className="mt-7 text-base md:text-lg text-[#8A91A5] max-w-2xl leading-relaxed">
            {m.hero.sub}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a data-testid="men-hero-cta-primary" href="#contact" className="btn-gold">{m.hero.ctaPrimary}</a>
            <a data-testid="men-hero-cta-secondary" href="#pillars" className="btn-ghost-dark">{m.hero.ctaSecondary}</a>
          </div>
        </div>
        <div className="lg:col-span-5 fade-up delay-2">
          <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#1A1D24] border border-[#2C313D]">
            <img
              src={heroImg}
              alt="Enrico Müller — fokussierte maskuline Präsenz"
              className="w-full h-full object-cover"
              style={{ filter: "saturate(0.55) contrast(1.18) brightness(0.92)", objectPosition: "center 25%" }}
            />
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#0F1014] to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[0.7rem] uppercase tracking-[0.24em] text-[#D4AF37]">
              <span>Focus</span>
              <span>Presence</span>
              <span>Power</span>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="px-6 md:px-12 lg:px-20 py-20 border-t border-[#2C313D]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="eyebrow text-[#8A91A5]">{m.problem.eyebrow}</span>
            <h2 className="mt-5 text-3xl md:text-4xl leading-tight text-white" style={{ fontFamily: 'Clash Display, Cabinet Grotesk, sans-serif', fontWeight: 600 }}>
              {m.problem.title}
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base md:text-lg text-[#8A91A5] leading-relaxed">{m.problem.body}</p>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
              {m.problem.bullets.map((b, i) => (
                <li key={i} data-testid={`men-bullet-${i}`} className="flex items-start gap-3 text-white">
                  <ChevronRight size={16} className="mt-1 text-[#D4AF37] shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="relative px-6 md:px-12 lg:px-20 py-20 border-t border-[#2C313D] overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center opacity-[0.18] pointer-events-none"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1542903660-eedba2cda473?auto=format&fit=crop&w=1600&q=80')", filter: "saturate(0.4) contrast(1.2) brightness(0.7)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F1014] via-[#0F1014]/85 to-[#0F1014] pointer-events-none" />
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative aspect-[4/5] overflow-hidden bg-[#1A1D24] border border-[#2C313D]">
              <img
                src={aboutImg}
                alt="Enrico Müller"
                className="w-full h-full object-cover"
                style={{ filter: "saturate(0.65) contrast(1.12) brightness(0.96)", objectPosition: "center 20%" }}
              />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <span className="eyebrow text-[#8A91A5]">{m.about.eyebrow}</span>
            <h2 className="mt-5 text-3xl md:text-4xl leading-tight text-white" style={{ fontFamily: 'Clash Display, Cabinet Grotesk, sans-serif', fontWeight: 600 }}>{m.about.title}</h2>
            <p className="mt-6 text-base md:text-lg text-[#8A91A5] leading-relaxed">{m.about.body}</p>
            <div className="mt-10 border-l-2 border-[#D4AF37] pl-6">
              <p className="text-lg md:text-xl text-white tracking-tight">{m.about.quote}</p>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section id="pillars" className="relative px-6 md:px-12 lg:px-20 py-20 border-t border-[#2C313D] overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center opacity-[0.14] pointer-events-none"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1600&q=80')", filter: "saturate(0.35) contrast(1.15) brightness(0.7)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F1014]/95 via-[#0F1014]/85 to-[#0F1014] pointer-events-none" />
        <div className="relative max-w-3xl">
          <span className="eyebrow text-[#8A91A5]">{m.pillars.eyebrow}</span>
          <h2 className="mt-5 text-3xl md:text-4xl text-white" style={{ fontFamily: 'Clash Display, Cabinet Grotesk, sans-serif', fontWeight: 600 }}>{m.pillars.title}</h2>
        </div>
        <div className="relative mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2C313D]">
          {m.pillars.items.map((p, i) => {
            const Icon = icons[i] || Brain;
            return (
              <div
                key={p.id}
                data-testid={`men-pillar-${p.id}`}
                className="bg-[#0F1014] p-10 hover:bg-[#15171D] transition-colors duration-200 group border border-transparent hover:border-[#D4AF37]"
              >
                <div className="flex items-center justify-between">
                  <Icon size={28} className="text-[#D4AF37]" />
                  <span className="text-[#2C313D] text-sm font-mono">0{i + 1}</span>
                </div>
                <h3 className="mt-8 text-xl md:text-2xl text-white leading-snug" style={{ fontFamily: 'Clash Display, Cabinet Grotesk, sans-serif', fontWeight: 600 }}>{p.title}</h3>
                <p className="mt-4 text-[#8A91A5] leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PROOF */}
      <section className="px-6 md:px-12 lg:px-20 py-20 border-t border-[#2C313D]">
        <span className="eyebrow text-[#8A91A5]">{m.proof.eyebrow}</span>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {m.proof.items.map((q, i) => (
            <figure key={i} data-testid={`men-quote-${i}`} className="p-8 bg-[#1A1D24] border border-[#2C313D]">
              <div className="text-[#D4AF37] text-3xl leading-none" style={{ fontFamily: 'Clash Display, sans-serif' }}>"</div>
              <blockquote className="mt-3 text-white leading-relaxed">{q}</blockquote>
            </figure>
          ))}
        </div>
      </section>

      {/* SHOP */}
      <Shop pathKey="men" />

      {/* BRIDGE — Inclusion */}
      <section className="relative px-6 md:px-12 lg:px-20 py-20 border-t border-[#2C313D] overflow-hidden">
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="eyebrow text-[#8A91A5]">{m.bridge.eyebrow}</span>
            <h2 className="mt-5 text-3xl md:text-4xl leading-tight text-white" style={{ fontFamily: 'Clash Display, Cabinet Grotesk, sans-serif', fontWeight: 600 }}>{m.bridge.title}</h2>
            <p className="mt-6 text-base md:text-lg text-[#8A91A5] leading-relaxed">{m.bridge.body}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-[#2C313D] bg-[#1A1D24] p-8">
              <p className="text-sm uppercase tracking-[0.22em] text-[#8A91A5]">{m.bridge.crossEyebrow}</p>
              <Link
                to="/women"
                data-testid="men-cross-link"
                className="mt-4 inline-flex items-center gap-2 text-white hover:text-[#D4AF37] transition group"
                style={{ fontFamily: 'Clash Display, Cabinet Grotesk, sans-serif', fontWeight: 600, fontSize: "1.4rem" }}
              >
                {m.bridge.crossLink}
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA / CONTACT */}
      <section id="contact" className="relative px-6 md:px-12 lg:px-20 py-20 border-t border-[#2C313D] overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center opacity-[0.18] pointer-events-none"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1600&q=80')", filter: "saturate(0.35) contrast(1.2) brightness(0.65)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1014] via-[#0F1014]/90 to-[#0F1014]/95 pointer-events-none" />
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow text-[#8A91A5]">{m.cta.eyebrow}</span>
            <h2 className="mt-5 text-3xl md:text-5xl leading-tight text-white" style={{ fontFamily: 'Clash Display, Cabinet Grotesk, sans-serif', fontWeight: 600 }}>{m.cta.title}</h2>
            <p className="mt-6 text-base md:text-lg text-[#8A91A5] leading-relaxed">{m.cta.sub}</p>
            {m.cta.investmentNote && (
              <p className="mt-4 text-sm text-[#D4AF37] italic">{m.cta.investmentNote}</p>
            )}
          </div>
          <div className="lg:col-span-7">
            <h3 className="text-sm uppercase tracking-[0.22em] text-[#D4AF37] mb-6">{m.cta.formTitle}</h3>
            <ContactForm pathKey="men" />
          </div>
        </div>
      </section>

      <Footer variant="men" />
    </div>
  );
};

export default MenPage;
